/**
 * PickForMe.jsx
 * 
 * AI-powered "Pick for me" 3-step wizard modal.
 * Step 1: What mood? (Pick 1-3)
 * Step 2: What city? (Pick 1)
 * Step 3: Price range
 * Then: Loading animation → Result
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
} from './ui/dialog';
import { 
  Sparkles, 
  MapPin, 
  Clock, 
  Heart,
  Zap,
  Coffee,
  PartyPopper,
  Users,
  TreePine,
  Crown,
  Wand2,
  ChevronRight,
  ChevronLeft,
  X,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const fetcher = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch');
  return res.json();
};

// Mood options with bigger visual presence
const MOODS = [
  { id: 'adventurous', label: 'Adventurous', icon: Zap, selectedClass: 'bg-orange-100 text-orange-700 border-orange-400 ring-2 ring-orange-200' },
  { id: 'romantic', label: 'Romantic', icon: Heart, selectedClass: 'bg-rose-100 text-rose-700 border-rose-400 ring-2 ring-rose-200' },
  { id: 'chill', label: 'Chill', icon: Coffee, selectedClass: 'bg-sky-100 text-sky-700 border-sky-400 ring-2 ring-sky-200' },
  { id: 'party', label: 'Party', icon: PartyPopper, selectedClass: 'bg-purple-100 text-purple-700 border-purple-400 ring-2 ring-purple-200' },
  { id: 'family', label: 'Family Fun', icon: Users, selectedClass: 'bg-blue-100 text-blue-700 border-blue-400 ring-2 ring-blue-200' },
  { id: 'nature', label: 'Nature', icon: TreePine, selectedClass: 'bg-emerald-100 text-emerald-700 border-emerald-400 ring-2 ring-emerald-200' },
  { id: 'luxury', label: 'Luxury', icon: Crown, selectedClass: 'bg-amber-100 text-amber-700 border-amber-400 ring-2 ring-amber-200' },
  { id: 'unique', label: 'Unique', icon: Wand2, selectedClass: 'bg-indigo-100 text-indigo-700 border-indigo-400 ring-2 ring-indigo-200' },
];

// Price ranges
const PRICE_RANGES = [
  { id: '$', label: '$', desc: 'Budget-friendly' },
  { id: '$$', label: '$$', desc: 'Mid-range' },
  { id: '$$$', label: '$$$', desc: 'Premium' },
];

const ANIMATION_DURATION = 3500;

const PickForMe = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Mood, 2: City, 3: Price
  const [selectedMoods, setSelectedMoods] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const pendingResult = useRef(null);

  const { data: citiesData } = useSWR(
    open ? `${API_URL}/api/marketplace/cities` : null,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 60000 }
  );
  const cities = citiesData?.cities || [];

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep(1);
      setSelectedMoods([]);
      setSelectedCity(null);
      setSelectedPrice(null);
      setResult(null);
      setError(null);
      setIsLoading(false);
      pendingResult.current = null;
    }
  }, [open]);

  const toggleMood = (moodId) => {
    setSelectedMoods(prev => {
      if (prev.includes(moodId)) return prev.filter(m => m !== moodId);
      if (prev.length >= 3) return [...prev.slice(1), moodId];
      return [...prev, moodId];
    });
  };

  const handleNext = () => {
    if (step === 1 && selectedMoods.length > 0) {
      setStep(2);
    } else if (step === 2 && selectedCity) {
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleFindAdventure = async () => {
    if (!selectedPrice) return;

    setIsLoading(true);
    setError(null);
    pendingResult.current = null;

    try {
      const response = await fetch(`${API_URL}/api/marketplace/pick-for-me`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          moods: selectedMoods,
          cities: [selectedCity],
          priceRange: selectedPrice,
          alwaysReturn: true
        })
      });

      const data = await response.json();
      
      if (data.success && data.service) {
        pendingResult.current = data.service;
      }
    } catch (err) {
      // Will handle below
    }

    // Show loading animation
    await new Promise(resolve => setTimeout(resolve, ANIMATION_DURATION));

    if (pendingResult.current) {
      setResult(pendingResult.current);
    } else {
      setError('No adventures found. Try different options!');
    }
    setIsLoading(false);
  };

  const handleViewExperience = () => {
    if (result?.id) {
      navigate(`/experience/${result.id}`);
      onOpenChange?.(false);
    }
  };

  const handleTryAgain = () => {
    setStep(1);
    setSelectedMoods([]);
    setSelectedCity(null);
    setSelectedPrice(null);
    setResult(null);
    setError(null);
  };

  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    return `${hours} hr${hours > 1 ? 's' : ''}`;
  };

  // Step indicator dots
  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[1, 2, 3].map(s => (
        <div
          key={s}
          className={`w-2 h-2 rounded-full transition-all ${
            s === step 
              ? 'w-6 bg-gradient-to-r from-indigo-500 to-purple-500' 
              : s < step 
                ? 'bg-indigo-400' 
                : 'bg-gray-200'
          }`}
        />
      ))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0 bg-white rounded-3xl overflow-hidden border-0 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 pr-14">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-200">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Pick for me</h2>
              <p className="text-sm text-gray-500">Let us find your perfect adventure</p>
            </div>
          </div>
          {/* Close button provided by DialogContent */}
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {/* Loading Animation */}
          {isLoading && (
            <div className="py-12 flex flex-col items-center justify-center">
              <div className="relative mb-6">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center animate-pulse">
                  <Sparkles className="w-8 h-8 text-white animate-spin" style={{ animationDuration: '2s' }} />
                </div>
                <div className="absolute inset-0 w-20 h-20 rounded-full border-4 border-indigo-200 animate-ping" style={{ animationDuration: '1.5s' }} />
              </div>
              <p className="text-lg font-semibold text-gray-900">Finding your adventure...</p>
              <p className="text-sm text-gray-500 mt-1">
                Searching in {selectedCity}
              </p>
              <div className="flex gap-1 mt-4">
                {selectedMoods.map((m, i) => {
                  const mood = MOODS.find(mood => mood.id === m);
                  return mood && (
                    <span 
                      key={m} 
                      className={`px-2 py-1 rounded-full text-xs ${mood.selectedClass.split(' ').slice(0, 2).join(' ')}`}
                    >
                      {mood.label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Result View */}
          {!isLoading && result && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <span className="text-3xl">🎉</span>
                <p className="text-lg font-semibold text-gray-900 mt-2">Perfect match found!</p>
              </div>
              
              <div 
                onClick={handleViewExperience}
                className="border border-gray-200 rounded-2xl overflow-hidden cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
              >
                {result.mainImage && (
                  <div className="aspect-video relative overflow-hidden bg-gray-100">
                    <img 
                      src={result.mainImage} 
                      alt={result.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 text-lg">{result.name}</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                    <MapPin className="w-3.5 h-3.5" />
                    <span>{result.city}</span>
                    {result.durationMinutes && (
                      <>
                        <span>•</span>
                        <Clock className="w-3.5 h-3.5" />
                        <span>{formatDuration(result.durationMinutes)}</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xl font-bold text-gray-900">
                      {formatPrice(result.price)}
                    </span>
                    <span className="text-sm text-indigo-600 font-medium flex items-center gap-1">
                      View details <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={handleTryAgain} className="flex-1 rounded-2xl h-12">
                  Try Again
                </Button>
                <Button 
                  onClick={handleViewExperience} 
                  className="flex-1 rounded-2xl h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                >
                  Book Now
                </Button>
              </div>
            </div>
          )}

          {/* Error State */}
          {!isLoading && error && !result && (
            <div className="py-8 text-center">
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={handleTryAgain} variant="outline" className="rounded-2xl">
                Try Different Options
              </Button>
            </div>
          )}

          {/* Step Wizard */}
          {!isLoading && !result && !error && (
            <>
              <StepIndicator />

              {/* Step 1: Mood Selection */}
              {step === 1 && (
                <div className="space-y-4">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-semibold text-gray-900">What mood are you feeling?</h3>
                    <p className="text-gray-500 mt-1">Pick up to 3</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {MOODS.map(mood => {
                      const Icon = mood.icon;
                      const isSelected = selectedMoods.includes(mood.id);
                      return (
                        <button
                          key={mood.id}
                          onClick={() => toggleMood(mood.id)}
                          className={`
                            flex items-center gap-3 px-4 py-4 rounded-2xl text-left
                            border transition-all
                            ${isSelected 
                              ? mood.selectedClass 
                              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'}
                          `}
                        >
                          <Icon className="w-5 h-5 flex-shrink-0" />
                          <span className="font-medium">{mood.label}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Next button appears only when mood selected */}
                  {selectedMoods.length > 0 && (
                    <Button 
                      onClick={handleNext}
                      className="w-full mt-6 py-6 text-base rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                    >
                      Next
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                  )}
                </div>
              )}

              {/* Step 2: City Selection */}
              {step === 2 && (
                <div className="space-y-4">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-semibold text-gray-900">Where do you want to go?</h3>
                    <p className="text-gray-500 mt-1">Pick a city</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 max-h-[280px] overflow-y-auto pr-1">
                    {cities.slice(0, 10).map(city => {
                      const isSelected = selectedCity === city.city;
                      return (
                        <button
                          key={city.city}
                          onClick={() => setSelectedCity(city.city)}
                          className={`
                            flex items-center gap-3 px-4 py-4 rounded-2xl text-left
                            border transition-all
                            ${isSelected 
                              ? 'bg-indigo-100 text-indigo-700 border-indigo-400 ring-2 ring-indigo-200' 
                              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'}
                          `}
                        >
                          <MapPin className="w-5 h-5 flex-shrink-0" />
                          <span className="font-medium">{city.city}</span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button 
                      variant="outline" 
                      onClick={handleBack}
                      className="flex-1 py-6 rounded-2xl"
                    >
                      <ChevronLeft className="w-5 h-5 mr-2" />
                      Back
                    </Button>
                    {selectedCity && (
                      <Button 
                        onClick={handleNext}
                        className="flex-1 py-6 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                      >
                        Next
                        <ChevronRight className="w-5 h-5 ml-2" />
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Step 3: Price Range */}
              {step === 3 && (
                <div className="space-y-4">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-semibold text-gray-900">What's your budget?</h3>
                    <p className="text-gray-500 mt-1">Pick a price range</p>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3">
                    {PRICE_RANGES.map(range => {
                      const isSelected = selectedPrice === range.id;
                      return (
                        <button
                          key={range.id}
                          onClick={() => setSelectedPrice(range.id)}
                          className={`
                            flex flex-col items-center justify-center py-6 rounded-2xl
                            border transition-all
                            ${isSelected 
                              ? 'bg-emerald-100 text-emerald-700 border-emerald-400 ring-2 ring-emerald-200' 
                              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'}
                          `}
                        >
                          <span className="text-2xl font-bold">{range.label}</span>
                          <span className="text-xs mt-1 opacity-70">{range.desc}</span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button 
                      variant="outline" 
                      onClick={handleBack}
                      className="py-6 px-6 rounded-2xl"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    {selectedPrice && (
                      <Button 
                        onClick={handleFindAdventure}
                        className="flex-1 py-6 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-base"
                      >
                        <Sparkles className="w-5 h-5 mr-2" />
                        Ready for an adventure?
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PickForMe;
